/* eslint-disable max-len */
import { JSX, useState } from 'react';
import './App.css';

import configureAmplify from './utils/amplifyConfig';
import Header from './components/Header';
import NavPanel from './controls/NavPanel';
import type { NavItem } from './models/LayoutTypes';
import CaseFolders from './components/CaseFolders';
import SiteDropdown from './controls/SiteDropDown';
import { getAppConfig } from './utils/appConfig';
import type { SiteModel } from './models/DataAdminTypes';
import FolderContents from './components/FolderContents';

import { useAuth } from './components/auth/authHelpers';

const config = getAppConfig();
configureAmplify();

function Dashboard({ onLogout }: {onLogout: () => void}): JSX.Element {
  const auth = useAuth();

  // enum for selected views
  const VIEWS = {
    CASE_FOLDERS: 'CASE_FOLDERS',
    FOLDER_CONTENTS: 'FOLDER_CONTENTS'
  };
  const [selectedSite, setSelectedSite] = useState<SiteModel | null>(null);
  const [selectedView, setSelectedView] = useState<string>(VIEWS.CASE_FOLDERS);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const handleSelectFolder = (folderName: string): void => {
    setSelectedView(VIEWS.FOLDER_CONTENTS);
    setSelectedFolder(folderName);
  };

  const handleSelectSite = (site: SiteModel): void => {
    setSelectedSite(site);
    setSelectedView(VIEWS.CASE_FOLDERS);
    setSelectedFolder(null);
  };
  
  const primaryNav: NavItem[] = [
    { 
      label: 'Case Uploads', 
      href: '#', 
      active: true,
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
<path d="M10.7417 4.60205C9.644 5.94985 9.14286 7.69267 9.14286 9.18519C9.14286 9.81929 8.63118 10.3333 8 10.3333C3.85867 10.3333 2.28571 13.2362 2.28571 14.9259C2.28571 15.9073 2.53266 17.0333 3.28866 17.9013C4.0174 18.7381 5.37998 19.5185 8 19.5185H13.5449C13.3337 20.2536 13.2017 21.0223 13.1585 21.8148H8C4.90574 21.8148 2.83974 20.8731 1.56848 19.4135C0.324481 17.9852 0 16.2408 0 14.9259C0 12.2214 2.20673 8.57659 6.92215 8.09123C7.11849 6.41978 7.76727 4.62798 8.97262 3.14795C10.4756 1.30251 12.8003 0 16 0C17.9429 0 20.2081 0.644682 22.0094 2.12527C23.6334 3.46017 24.835 5.4404 25.0918 8.10279C26.6663 8.29225 28.1928 8.88709 29.4128 9.83358C30.929 11.0098 32 12.7605 32 14.9259C32 15.4576 31.9535 15.973 31.8586 16.4667C31.2428 15.5837 30.4931 14.8019 29.639 14.1511C29.4406 13.1537 28.8601 12.306 28.0158 11.6511C26.9452 10.8205 25.4812 10.3333 24 10.3333C23.3688 10.3333 22.8571 9.81929 22.8571 9.18519C22.8571 6.65891 21.8679 4.97594 20.562 3.90251C19.2204 2.79976 17.4856 2.2963 16 2.2963C13.4854 2.2963 11.8101 3.29008 10.7417 4.60205ZM32 22.3889C32 17.6331 28.1624 13.7778 23.4286 13.7778C18.6947 13.7778 14.8571 17.6331 14.8571 22.3889C14.8571 27.1447 18.6947 31 23.4286 31C28.1624 31 32 27.1447 32 22.3889ZM23.1082 16.3263C23.4429 15.99 23.9856 15.99 24.3204 16.3263L28.8918 20.9189C29.2265 21.2552 29.2265 21.8004 28.8918 22.1367C28.5571 22.473 28.0144 22.473 27.6796 22.1367L24.5714 19.0141V27.8426C24.5714 28.3182 24.1877 28.7037 23.7143 28.7037C23.2409 28.7037 22.8571 28.3182 22.8571 27.8426V19.0141L19.749 22.1367C19.4142 22.473 18.8715 22.473 18.5368 22.1367C18.202 21.8004 18.202 21.2552 18.5368 20.9189L23.1082 16.3263Z" fill="#242424"/>
</svg> `
    }
  ];

  const handleLogout = (): void => {
    auth?.logout();
    onLogout();
  };

  return (
    <div className="min-h-screen">
      <Header primaryNav={primaryNav} onLogout={handleLogout} />

      {/* Content container */}
      <div className="mx-auto max-w-7xl min-w-[70vw] px-3 py-4">

        {/* Desktop grid: 3 columns; Mobile: main only (drawers for navs) */}
        <div className="lg:grid lg:grid-cols-[10rem_1fr] lg:gap-2">
          {/* Primary nav (desktop) */}
          <aside className="hidden lg:block">
            <NavPanel title="" items={primaryNav} />
          </aside>

          {/* Main content */}
          <main className="min-h-[60vh]">
            {/* Site selection dropdown (desktop) */}
            <div className="hidden lg:block mb-4">
              <div className="card p-2">
                <SiteDropdown
                  getAccessToken={auth.getAccessToken}
                  className="w-full"
                  env={config.ENV}
                  label="Select Site"
                  placeholder="Choose a site"
                  helperText="Select a site to continue"
                  onSiteSelected={(site: SiteModel) => handleSelectSite(site)}
                />
              </div>
            </div>

            <section>
              <h2 className="justify-center flex">
                {selectedSite?.siteName}
              </h2>

              <div className="muted">
                {selectedView === VIEWS.CASE_FOLDERS && (
                  <CaseFolders 
                    getAccessToken={auth.getAccessToken}
                    env={config.ENV}
                    site={selectedSite}
                    onSelectFolder={handleSelectFolder}/>
                )}

                {selectedView === VIEWS.FOLDER_CONTENTS && (
                  <FolderContents
                    getAccessToken={auth.getAccessToken}
                    env={config.ENV}
                    site={selectedSite}
                    caseFolder={selectedFolder || ''}
                    onBack={() => setSelectedView(VIEWS.CASE_FOLDERS)}
                  />
                )}
                
              </div>

            </section>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
